import { useMsal } from '@azure/msal-react'
import {
  ArrowRight,
  Bath,
  Bed,
  Bookmark,
  BookmarkCheck,
  Building2,
  DollarSign,
  GraduationCap,
  Home,
  MapPin,
  Ruler,
  Share2,
  Trash2,
  TrendingUp,
  TriangleAlert,
  X,
} from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { PoweredByFooter } from '@/components/PoweredByFooter'
import { ServiceBanner } from '@/components/ServiceBanner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useBanner } from '@/hooks/useBanner'
import { trackEvent } from '@/lib/analytics'

// ── Types ──────────────────────────────────────────────────────
interface SalesHistory {
  SaleDate_Date: string | null
  SalePrice: number | null
  SaleMethod: string | null
  IsSettled: boolean | null
}

interface SchoolZone {
  SchoolName: string
  Decile: number | null
  Type: string
  Gender: string
  Zoned: string
}

export interface PropertyDetails {
  councilValuation: number | null
  landValue: number | null
  improvementValue: number | null
  revisionDate: string | null
  salesHistory: SalesHistory[]
  schoolZones: SchoolZone[]
}

interface SavedProperty {
  address: string
  rafid: number
  low: number
  high: number
  rating: number
  savedAt: string
}

// ── API helpers ────────────────────────────────────────────────
async function fetchSavedProperties(userId: string): Promise<SavedProperty[]> {
  try {
    const r = await fetch(
      `api/user-saved-items?userId=${encodeURIComponent(userId)}&itemType=property`,
    )
    const items: any[] = await r.json()
    return items.map((i) => JSON.parse(i.itemData) as SavedProperty)
  } catch {
    return []
  }
}

async function apiSaveProperty(userId: string, prop: SavedProperty): Promise<boolean> {
  try {
    const r = await fetch('api/user-saved-items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        itemType: 'property',
        itemId: String(prop.rafid),
        itemData: JSON.stringify(prop),
      }),
    })
    return r.ok
  } catch {
    return false
  }
}

async function apiRemoveProperty(userId: string, rafid: number): Promise<boolean> {
  try {
    const r = await fetch(
      `api/user-saved-items?userId=${encodeURIComponent(userId)}&itemType=property&itemId=${encodeURIComponent(String(rafid))}`,
      { method: 'DELETE' },
    )
    return r.ok
  } catch {
    return false
  }
}

// ── Helpers ────────────────────────────────────────────────────
function formatPrice(price: number) {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    maximumFractionDigits: 0,
  }).format(price)
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function RatingBadge({ rating, onDark = false }: { rating: number; onDark?: boolean }) {
  const color = onDark
    ? 'bg-white/20 text-white border border-white/40'
    : rating >= 4
      ? 'bg-emerald-100 text-emerald-700'
      : rating === 3
        ? 'bg-amber-100 text-amber-700'
        : 'bg-red-100 text-red-700'
  const label = rating >= 4 ? 'High' : rating === 3 ? 'Medium' : 'Low'
  return (
    <span
      data-confidence-badge={rating}
      className={`px-2 py-1 rounded-md text-xs font-medium inline-block mt-2 ${color}`}
    >
      {label} Confidence
    </span>
  )
}

// ── Saved Properties Tab ───────────────────────────────────────
function SavedPropertiesTab({
  partyId,
  onRemove,
  onOpen,
}: {
  partyId: string
  onRemove?: (rafid: number) => void
  onOpen: (prop: SavedProperty) => void
}) {
  const [saved, setSaved] = useState<SavedProperty[]>([])

  useEffect(() => {
    if (!partyId) return
    fetchSavedProperties(partyId).then(setSaved)
  }, [partyId])

  const handleRemove = async (rafid: number) => {
    const ok = await apiRemoveProperty(partyId, rafid)
    if (ok) {
      setSaved((prev) => prev.filter((p) => p.rafid !== rafid))
      onRemove?.(rafid)
    }
  }

  if (saved.length === 0) {
    return (
      <Card className="p-6 text-center text-muted-foreground text-sm">
        No saved properties yet. Run an estimate and tap Save to keep it here.
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Saved to your account. Available across all devices. Tap a property to reopen it with its
        current estimate.
      </p>
      {saved.map((p) => (
        <Card
          key={p.rafid}
          onClick={() => onOpen(p)}
          className="p-4 cursor-pointer hover:border-primary/40 transition-colors"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{p.address}</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleRemove(p.rafid)
              }}
              className="text-muted-foreground hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div>
              <p className="text-base font-bold text-primary">
                {formatPrice(p.low)} – {formatPrice(p.high)}
              </p>
              <p className="text-3xs text-muted-foreground mt-0.5">
                Estimate as at {formatDate(p.savedAt)}
              </p>
            </div>
            <RatingBadge rating={p.rating} />
          </div>
        </Card>
      ))}
    </div>
  )
}

// ── Main Screen ────────────────────────────────────────────────
interface PropertyToolsScreenProps {
  initialTab?: string
  valAddress: string
  setValAddress: React.Dispatch<React.SetStateAction<string>>
  selectedRafid: number | null
  setSelectedRafid: React.Dispatch<React.SetStateAction<number | null>>
  valSuburb: string
  setValSuburb: React.Dispatch<React.SetStateAction<string>>
  valBedrooms: string
  setValBedrooms: React.Dispatch<React.SetStateAction<string>>
  valBathrooms: string
  setValBathrooms: React.Dispatch<React.SetStateAction<string>>
  valFloorArea: string
  setValFloorArea: React.Dispatch<React.SetStateAction<string>>
  valLandArea: string
  setValLandArea: React.Dispatch<React.SetStateAction<string>>
  valGarages: string
  setValGarages: React.Dispatch<React.SetStateAction<string>>
  valResult: { low: number; high: number; rating: number } | null
  setValResult: React.Dispatch<
    React.SetStateAction<{ low: number; high: number; rating: number } | null>
  >
  propertyDetails: PropertyDetails | null
  setPropertyDetails: React.Dispatch<React.SetStateAction<PropertyDetails | null>>
}

export function PropertyToolsScreen({
  initialTab,
  valAddress,
  setValAddress,
  selectedRafid,
  setSelectedRafid,
  valSuburb,
  setValSuburb,
  valBedrooms,
  setValBedrooms,
  valBathrooms,
  setValBathrooms,
  valFloorArea,
  setValFloorArea,
  valLandArea,
  setValLandArea,
  valGarages,
  setValGarages,
  valResult,
  setValResult,
  propertyDetails,
  setPropertyDetails,
}: PropertyToolsScreenProps) {
  const banner = useBanner('estimate')
  const { accounts } = useMsal()
  const partyId = accounts[0]?.localAccountId ?? ''
  const [activeTab, setActiveTab] = useState(initialTab ?? 'valuation')
  const resultsRef = useRef<HTMLDivElement>(null)
  const scrollToResultsRef = useRef<HTMLDivElement>(null)
  const [isSharing, setIsSharing] = useState(false)
  const [shareError, setShareError] = useState<string | null>(null)

  const handleShare = async () => {
    if (!resultsRef.current) return
    setIsSharing(true)
    setShareError(null)
    try {
      // Show pdf-only elements so their dimensions are included in measurements
      const pdfOnlyEls = Array.from(
        resultsRef.current.querySelectorAll<HTMLElement>('[data-pdf-only]'),
      )
      pdfOnlyEls.forEach((el) => {
        el.style.display = 'block'
      })
      // Force layout recalculation
      resultsRef.current.getBoundingClientRect()
      // Measure card boundaries before html2canvas so page breaks fall between cards
      const containerRect = resultsRef.current.getBoundingClientRect()
      const childBottoms = Array.from(resultsRef.current.children).map((child) => {
        return child.getBoundingClientRect().bottom - containerRect.top
      })
      // Capture badge position before html2canvas so we can draw it manually in jsPDF
      type BadgeInfo = {
        text: string
        bg: string
        fg: string
        top: number
        left: number
        width: number
        height: number
      }
      const badgeEl = resultsRef.current.querySelector<HTMLElement>('[data-confidence-badge]')
      let badgeInfo: BadgeInfo | null = null
      if (badgeEl) {
        const br = badgeEl.getBoundingClientRect()
        const rating = parseInt(badgeEl.getAttribute('data-confidence-badge') || '0')
        badgeInfo = {
          text:
            rating >= 4 ? 'High Confidence' : rating === 3 ? 'Medium Confidence' : 'Low Confidence',
          bg: rating >= 4 ? '#d1fae5' : rating === 3 ? '#fef3c7' : '#fee2e2',
          fg: rating >= 4 ? '#047857' : rating === 3 ? '#b45309' : '#b91c1c',
          top: br.top - containerRect.top,
          left: br.left - containerRect.left,
          width: br.width,
          height: br.height,
        }
        badgeEl.style.visibility = 'hidden'
      }
      const saleMethodEl = resultsRef.current.querySelector<HTMLElement>('[data-sale-method-badge]')
      let saleMethodInfo: BadgeInfo | null = null
      if (saleMethodEl) {
        const br = saleMethodEl.getBoundingClientRect()
        saleMethodInfo = {
          text: saleMethodEl.textContent || '',
          bg: '#e5e7eb',
          fg: '#374151',
          top: br.top - containerRect.top,
          left: br.left - containerRect.left,
          width: br.width,
          height: br.height,
        }
        saleMethodEl.style.visibility = 'hidden'
      }
      // Hide pdf-only elements again — onclone will re-show them during capture
      pdfOnlyEls.forEach((el) => {
        el.style.display = 'none'
      })
      // Pre-inline all browser-computed colours so html2canvas never needs to parse
      // CSS variables or modern hsl() syntax — both of which it handles inconsistently.
      const allEls = [
        resultsRef.current,
        ...Array.from(resultsRef.current.querySelectorAll<HTMLElement>('*')),
      ]
      const savedCssTexts = allEls.map((el) => el.style.cssText)
      allEls.forEach((el) => {
        const cs = window.getComputedStyle(el)
        el.style.color = cs.color
        const bg = cs.backgroundColor
        if (bg && bg !== 'rgba(0, 0, 0, 0)') el.style.backgroundColor = bg
        el.style.borderTopColor = cs.borderTopColor
        el.style.borderRightColor = cs.borderRightColor
        el.style.borderBottomColor = cs.borderBottomColor
        el.style.borderLeftColor = cs.borderLeftColor
        el.style.borderRadius = cs.borderRadius
        // Prevent parent overflow:hidden from clipping child badges
        el.style.overflow = 'visible'
        // Freeze animations at end state so opacity/transform are fully resolved
        el.style.animation = 'none'
        el.style.opacity = cs.opacity
        el.style.transform = cs.transform
        if (
          (cs.display === 'inline' || cs.display === 'inline-flex') &&
          cs.borderRadius !== '0px'
        ) {
          el.style.display = 'inline-block'
          el.style.padding = cs.padding
          el.style.lineHeight = cs.lineHeight
        }
      })
      // Confidence pill: bg-*/text-* use CSS variables so getComputedStyle may return
      // transparent — map Tailwind class names to known hex values instead.
      resultsRef.current.querySelectorAll<HTMLElement>('span.rounded-md').forEach((el) => {
        const cs = window.getComputedStyle(el)
        const cs2 = window.getComputedStyle(el)
        const bg = el.classList.contains('bg-amber-100')
          ? '#fef3c7'
          : el.classList.contains('bg-red-100')
            ? '#fee2e2'
            : el.classList.contains('bg-green-100')
              ? '#dcfce7'
              : el.classList.contains('bg-emerald-100')
                ? '#d1fae5'
                : cs2.backgroundColor
        const fg = el.classList.contains('text-amber-700')
          ? '#b45309'
          : el.classList.contains('text-red-700')
            ? '#b91c1c'
            : el.classList.contains('text-green-700')
              ? '#15803d'
              : el.classList.contains('text-emerald-700')
                ? '#047857'
                : cs2.color
        el.style.display = 'inline-block'
        el.style.backgroundColor = bg
        el.style.color = fg
        el.style.borderRadius = cs.borderRadius
        el.style.padding = cs.padding
        el.style.fontSize = cs.fontSize
        el.style.fontWeight = cs.fontWeight
        el.style.lineHeight = cs.lineHeight
      })
      // Gradient cards: html2canvas renders gradients lighter than CSS (linear vs sRGB
      // interpolation). Must be applied BEFORE html2canvas captures the DOM.
      resultsRef.current.querySelectorAll<HTMLElement>('.bg-gradient-reinz').forEach((el) => {
        el.style.setProperty('background-image', 'none', 'important')
        el.style.setProperty('background-color', '#4c2c8f', 'important')
      })
      const html2canvas = (await import('html2canvas')).default
      const { jsPDF } = await import('jspdf')
      const canvas = await html2canvas(resultsRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (doc) => {
          doc.querySelectorAll<HTMLElement>('[data-no-capture]').forEach((el) => {
            el.style.display = 'none'
          })
          doc.querySelectorAll<HTMLElement>('[data-pdf-only]').forEach((el) => {
            el.style.display = 'block'
          })
        },
      })
      // Restore all inline styles — CSS classes take over again
      allEls.forEach((el, i) => {
        el.style.cssText = savedCssTexts[i]
      })
      if (badgeEl) badgeEl.style.visibility = ''
      if (saleMethodEl) saleMethodEl.style.visibility = ''
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = pageWidth - 40
      const headerH = 100 // ~36mm — matches Market Insights header height
      const contentTop = headerH + 10
      const availableHeight = pageHeight - contentTop - 22 // reserve space for disclaimer at bottom

      // Convert child bottom edges (DOM px) to canvas pixels so we can slice exactly
      const scaleToCanvas = canvas.width / resultsRef.current.offsetWidth
      const cutPointsCanvas = childBottoms.map((b) => Math.round(b * scaleToCanvas))
      const pageHeightCanvas = Math.round((availableHeight * canvas.width) / imgWidth)

      // Build non-overlapping canvas slices that end at card boundaries
      const slices: { start: number; end: number }[] = []
      let startRow = 0
      while (startRow < canvas.height) {
        const hardEnd = startRow + pageHeightCanvas
        if (hardEnd >= canvas.height) {
          slices.push({ start: startRow, end: canvas.height })
          break
        }
        const validCuts = cutPointsCanvas.filter((p) => p > startRow && p <= hardEnd)
        const cutRow = validCuts.length > 0 ? validCuts[validCuts.length - 1] : hardEnd
        slices.push({ start: startRow, end: cutRow })
        startRow = cutRow
      }

      // Pre-load brand + profile images as data URLs (via backend proxy to avoid CORS)
      const fetchImageDataUrl = async (url: string): Promise<string | null> => {
        try {
          const resp = await fetch(url)
          if (!resp.ok) return null
          const blob = await resp.blob()
          return await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
        } catch {
          return null
        }
      }

      const makeCircularDataUrl = (dataUrl: string, size: number, offsetY = 50): Promise<string> =>
        new Promise((resolve, reject) => {
          const img = new Image()
          img.onerror = reject
          img.onload = () => {
            const c = document.createElement('canvas')
            c.width = size
            c.height = size
            const ctx = c.getContext('2d')!
            ctx.beginPath()
            ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
            ctx.closePath()
            ctx.clip()
            const s = Math.min(img.width, img.height)
            const sx = (img.width - s) / 2
            const sy = (img.height - s) * (offsetY / 100)
            ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size)
            resolve(c.toDataURL('image/png'))
          }
          img.src = dataUrl
        })

      let brandDataUrl: string | null = null
      let headshotDataUrl: string | null = null
      let pdfTagline = ''
      let agentName = ''
      let agentContact = ''
      let agentOrg = ''
      if (partyId) {
        const [brand, headshot, taglineRes, offsetRes, profileRes] = await Promise.all([
          fetchImageDataUrl(`/api/userimage/brand/data?userId=${encodeURIComponent(partyId)}`),
          fetchImageDataUrl(`/api/userimage/profile/data?userId=${encodeURIComponent(partyId)}`),
          fetch(`/api/user-preferences/pdf_tagline?userId=${encodeURIComponent(partyId)}`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
          fetch(`/api/user-preferences/profile_pic_offset_y?userId=${encodeURIComponent(partyId)}`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
          fetch(`api/profile/profiledata?query=${partyId}`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        ])
        brandDataUrl = brand
        const offsetY = offsetRes?.value ? parseFloat(offsetRes.value) : 50
        headshotDataUrl = headshot ? await makeCircularDataUrl(headshot, 120, offsetY) : null
        pdfTagline = taglineRes?.value ?? ''
        const pd = profileRes?.data
        if (pd) {
          agentName = pd.fullName ?? ''
          agentOrg = pd.organization ?? ''
          const phone = pd.mobilePhone || pd.workPhone || ''
          agentContact = [pd.primaryEmail, phone].filter(Boolean).join('  |  ')
        }
      }

      for (let i = 0; i < slices.length; i++) {
        // REINZ branded header on every page
        if (i > 0) pdf.addPage()
        pdf.setFillColor(76, 44, 143)
        pdf.rect(0, 0, pageWidth, headerH, 'F')
        pdf.setTextColor(255, 255, 255)

        // Headshot (circular) on the left
        const hsSize = 68
        const hsX = 14
        const hsY = (headerH - hsSize) / 2
        const textX = headshotDataUrl ? hsX + hsSize + 10 : 20
        if (headshotDataUrl) {
          pdf.addImage(headshotDataUrl, 'PNG', hsX, hsY, hsSize, hsSize)
        }

        // Brand image right-aligned (reserve space for text block)
        const brandW = 100
        const brandH = 50
        if (brandDataUrl) {
          const fmt = brandDataUrl.startsWith('data:image/png')
            ? 'PNG'
            : brandDataUrl.startsWith('data:image/webp')
              ? 'WEBP'
              : 'JPEG'
          pdf.addImage(
            brandDataUrl,
            fmt,
            pageWidth - 16 - brandW,
            (headerH - brandH) / 2,
            brandW,
            brandH,
          )
        }
        const textMaxW = brandDataUrl ? pageWidth - 16 - brandW - textX - 6 : pageWidth - textX - 16

        // Organisation
        pdf.setFontSize(13)
        pdf.setFont('helvetica', 'bold')
        pdf.text(agentOrg || 'REINZ', textX, 28, { maxWidth: textMaxW })

        // Agent name
        if (agentName) {
          pdf.setFontSize(11)
          pdf.setFont('helvetica', 'bold')
          pdf.text(agentName, textX, 44, { maxWidth: textMaxW })
        }

        // Contact
        if (agentContact) {
          pdf.setFontSize(8)
          pdf.setFont('helvetica', 'normal')
          pdf.setTextColor(220, 210, 255)
          pdf.text(agentContact, textX, 56, { maxWidth: textMaxW })
        }

        // Tagline
        if (pdfTagline) {
          pdf.setFontSize(8)
          pdf.setFont('helvetica', 'bolditalic')
          pdf.setTextColor(255, 255, 255)
          pdf.text(pdfTagline, textX, 68, { maxWidth: textMaxW })
        }

        if (!brandDataUrl) {
          pdf.setFontSize(8)
          pdf.setFont('helvetica', 'normal')
          pdf.setTextColor(200, 200, 200)
          pdf.text(
            new Date().toLocaleDateString('en-NZ', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
            pageWidth - 16,
            28,
            { align: 'right' },
          )
        }

        pdf.setTextColor(0, 0, 0)

        const { start, end } = slices[i]
        const sliceCanvas = document.createElement('canvas')
        sliceCanvas.width = canvas.width
        sliceCanvas.height = end - start
        sliceCanvas
          .getContext('2d')!
          .drawImage(canvas, 0, start, canvas.width, end - start, 0, 0, canvas.width, end - start)
        const sliceHeight = ((end - start) * imgWidth) / canvas.width
        pdf.addImage(
          sliceCanvas.toDataURL('image/png'),
          'PNG',
          20,
          contentTop,
          imgWidth,
          sliceHeight,
        )
        // Draw badges manually — avoids html2canvas clipping on rounded elements
        const drawBadge = (info: BadgeInfo, pageIndex: number) => {
          const scale = imgWidth / resultsRef.current!.offsetWidth
          const domScale = canvas.width / resultsRef.current!.offsetWidth
          const bx = 20 + info.left * scale
          const by = contentTop + (info.top - slices[pageIndex].start / domScale) * scale
          const bw = info.width * scale
          const bh = info.height * scale
          pdf.setFillColor(info.bg)
          pdf.roundedRect(bx, by, bw, bh, 3, 3, 'F')
          pdf.setTextColor(info.fg)
          pdf.setFontSize(9)
          pdf.setFont('helvetica', 'bold')
          pdf.text(info.text, bx + bw / 2, by + bh / 2 + 3, { align: 'center' })
          pdf.setTextColor(0, 0, 0)
        }
        if (badgeInfo) {
          const domScale = canvas.width / resultsRef.current.offsetWidth
          const badgePageIndex = slices.findIndex(
            (s) => badgeInfo!.top * domScale >= s.start && badgeInfo!.top * domScale < s.end,
          )
          if (badgePageIndex === i) drawBadge(badgeInfo, i)
        }
        if (saleMethodInfo) {
          const domScale = canvas.width / resultsRef.current.offsetWidth
          const salePageIndex = slices.findIndex(
            (s) =>
              saleMethodInfo!.top * domScale >= s.start && saleMethodInfo!.top * domScale < s.end,
          )
          if (salePageIndex === i) drawBadge(saleMethodInfo, i)
        }

        // Disclaimer — small grey text above footer
        pdf.setFontSize(7.5)
        pdf.setFont('helvetica', 'normal')
        pdf.setTextColor(120, 120, 120)
        const disclaimer =
          'This report is an automated estimate only. It is not a registered valuation or Current Market Appraisal (CMA). It has been generated without a physical inspection of the property. REINZ does not warrant the accuracy or completeness of the data underlying the estimate.'
        const disclaimerLines = pdf.splitTextToSize(disclaimer, pageWidth - 40)
        pdf.text(disclaimerLines, 20, pageHeight - 18)
      }
      const pdfBlob = pdf.output('blob')
      const safeName =
        valAddress.replace(/[^a-zA-Z0-9]/g, '_').replace(/^_+|_+$/g, '') || 'property'
      const fileName = `${safeName}_estimate.pdf`
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' })
      if (pdfBlob.size === 0) throw new Error('PDF blob is empty')
      const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent)
      if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Property Valuation', text: valAddress })
        trackEvent('share_valuation', { address: valAddress, method: 'native' })
      } else {
        const url = URL.createObjectURL(pdfBlob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        trackEvent('share_valuation', { address: valAddress, method: 'download' })
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error('Share failed:', err)
        setShareError('Could not share. Please try again.')
      }
    } finally {
      setIsSharing(false)
    }
  }

  const [addressSuggestions, setAddressSuggestions] = useState<
    { rafid: number; address: string }[]
  >([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isLoadingAddress, setIsLoadingAddress] = useState(false)
  const addressInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    trackEvent('screen_viewed', { screen: 'tools' })
  }, [])

  const handleClearAddress = () => {
    latestRafidRequestRef.current = null
    setValAddress('')
    setSelectedRafid(null)
    setValResult(null)
    setPropertyDetails(null)
    setNoValuationData(false)
    setValSuburb('')
    setValBedrooms('')
    setValBathrooms('')
    setValFloorArea('')
    setValLandArea('')
    setValGarages('')
    setAddressSuggestions([])
    setShowSuggestions(false)
    addressInputRef.current?.focus()
  }

  const [isLoadingEstimate, setIsLoadingEstimate] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedbackBedrooms, setFeedbackBedrooms] = useState('')
  const [feedbackBathrooms, setFeedbackBathrooms] = useState('')
  const [feedbackFloorArea, setFeedbackFloorArea] = useState('')
  const [feedbackLandArea, setFeedbackLandArea] = useState('')
  const [feedbackGarages, setFeedbackGarages] = useState('')
  const [feedbackComment, setFeedbackComment] = useState('')
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false)
  const [feedbackError, setFeedbackError] = useState('')
  const [savedRafids, setSavedRafids] = useState<Set<number>>(new Set())
  const [noValuationData, setNoValuationData] = useState(false)

  useEffect(() => {
    if (!partyId) return
    fetchSavedProperties(partyId).then((props) =>
      setSavedRafids(new Set(props.map((p) => p.rafid))),
    )
  }, [partyId])

  useEffect(() => {
    setIsSaved(selectedRafid !== null && savedRafids.has(selectedRafid))
  }, [selectedRafid, savedRafids])

  const fetchAddressSuggestions = async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([])
      return
    }
    setIsLoadingAddress(true)
    try {
      const response = await fetch(`/api/address/search?Query=${encodeURIComponent(query)}`)
      const data = await response.json()
      setAddressSuggestions(
        data.map((item: any) => ({ rafid: item.rafid, address: item.addresssearch })),
      )
      setShowSuggestions(true)
    } catch (error) {
      console.error('Address fetch failed:', error)
    } finally {
      setIsLoadingAddress(false)
    }
  }

  // Tracks which rafid the user most recently asked to see, so that if two fetches are ever
  // in flight at once (e.g. rapidly tapping two saved properties), the slower/stale response
  // can't overwrite the fields after a newer selection has already been made.
  const latestRafidRequestRef = useRef<number | null>(null)

  const fetchPropertyDetails = async (rafid: number) => {
    try {
      const response = await fetch(`/api/property/search?rafid=${rafid}`)
      const data = await response.json()
      if (latestRafidRequestRef.current !== rafid) return data
      setValBedrooms(data.Bedrooms?.toString() || '')
      setValBathrooms(data.Bathrooms?.toString() || '')
      setValFloorArea(data.FloorArea?.toString() || '')
      setValLandArea(data.LandArea?.toString() || '')
      setValGarages(data.Garages?.toString() || '')
      setValSuburb(data.SuburbName || '')
      return data
    } catch (error) {
      console.error('Property details fetch failed:', error)
      return null
    }
  }

  const loadValuationForRafid = async (rafid: number) => {
    latestRafidRequestRef.current = rafid
    setIsLoadingEstimate(true)
    setNoValuationData(false)
    setFeedbackSubmitted(false)
    setFeedbackError('')
    try {
      const data = await fetchPropertyDetails(rafid)
      if (!data || latestRafidRequestRef.current !== rafid) return
      const avm = data.AVM
      // If both estimates are 0 or null/undefined, AVM has no data for this property
      setValResult(
        !avm?.LowEstimate && !avm?.HighEstimate
          ? null
          : { low: avm.LowEstimate, high: avm.HighEstimate, rating: avm.Rating },
      )
      setPropertyDetails({
        councilValuation: data.CouncilValuation ?? null,
        landValue: data.LandValue ?? null,
        improvementValue: data.ImprovementValue ?? null,
        revisionDate: data.RevisionDate ?? null,
        salesHistory: data.SalesHistory ?? [],
        schoolZones: Array.isArray(data.SchoolZones) ? data.SchoolZones : [],
      })
      if (avm?.LowEstimate || avm?.HighEstimate) {
        setTimeout(
          () => scrollToResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
          100,
        )
      } else {
        setNoValuationData(true)
      }
    } catch (error) {
      console.error('Property fetch failed:', error)
    } finally {
      if (latestRafidRequestRef.current === rafid) setIsLoadingEstimate(false)
    }
  }

  const handleValuationSubmit = async () => {
    trackEvent('valuation_requested', { address: valAddress })
    if (!selectedRafid) return
    setShowFeedback(false)
    setFeedbackSubmitted(false)
    setFeedbackBedrooms('')
    setFeedbackBathrooms('')
    setFeedbackFloorArea('')
    setFeedbackLandArea('')
    setFeedbackGarages('')
    setFeedbackComment('')
    await loadValuationForRafid(selectedRafid)
  }

  // Reopen a saved property: switch to the Estimate tab and re-fetch its current
  // valuation, rather than trusting the frozen low/high snapshot from save-time.
  const handleLoadSavedProperty = (prop: SavedProperty) => {
    trackEvent('saved_property_reopened', { rafid: prop.rafid })
    setSelectedRafid(prop.rafid)
    setValAddress(prop.address)
    setValResult(null)
    setPropertyDetails(null)
    setActiveTab('valuation')
    loadValuationForRafid(prop.rafid)
  }

  const handleSave = async () => {
    if (!valResult || !selectedRafid || !partyId) return
    const prop: SavedProperty = {
      address: valAddress,
      rafid: selectedRafid,
      low: valResult.low,
      high: valResult.high,
      rating: valResult.rating,
      savedAt: new Date().toISOString(),
    }
    const ok = await apiSaveProperty(partyId, prop)
    if (ok) {
      setIsSaved(true)
      setSavedRafids((prev) => new Set([...prev, selectedRafid]))
      trackEvent('item_saved', { itemType: 'property', itemId: String(selectedRafid) })
    }
  }

  return (
    <div className="pb-4">
      <ServiceBanner banner={banner} />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-2 mb-6">
          <TabsTrigger value="valuation" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Estimate
          </TabsTrigger>
          <TabsTrigger value="saved" className="flex items-center gap-2">
            <Bookmark className="w-4 h-4" />
            Saved
          </TabsTrigger>
          {/* <TabsTrigger value="cma" className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            CMA
          </TabsTrigger> */}
        </TabsList>

        {/* Valuation Tab */}
        <TabsContent value="valuation" className="space-y-4">
          {/* ── Form view ── */}
          <Card className="p-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="address">Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                  <Input
                    ref={addressInputRef}
                    id="address"
                    value={valAddress}
                    onChange={(e) => {
                      setValAddress(e.target.value)
                      setSelectedRafid(null)
                      setValResult(null)
                      setPropertyDetails(null)
                      setNoValuationData(false)
                      setValSuburb('')
                      setValBedrooms('')
                      setValBathrooms('')
                      setValFloorArea('')
                      setValLandArea('')
                      setValGarages('')
                      fetchAddressSuggestions(e.target.value)
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="pl-10 pr-9"
                    placeholder="Start typing an address..."
                  />
                  {valAddress && (
                    <button
                      type="button"
                      onClick={handleClearAddress}
                      aria-label="Clear address"
                      className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {showSuggestions && (isLoadingAddress || addressSuggestions.length > 0) && (
                    <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {isLoadingAddress ? (
                        <div className="p-3 text-sm text-muted-foreground">Searching...</div>
                      ) : (
                        addressSuggestions.map((item) => (
                          <button
                            key={item.rafid}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors flex items-center gap-2"
                            onMouseDown={() => {
                              setValAddress(item.address)
                              setSelectedRafid(item.rafid)
                              setShowSuggestions(false)
                              latestRafidRequestRef.current = item.rafid
                              fetchPropertyDetails(item.rafid)
                            }}
                          >
                            <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                            {item.address}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Suburb</Label>
                  <Input
                    type="string"
                    value={valSuburb}
                    onChange={(e) => setValSuburb(e.target.value)}
                    readOnly
                    className="bg-muted/50 cursor-default"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bedrooms</Label>
                  <div className="relative">
                    <Bed className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={valBedrooms}
                      onChange={(e) => setValBedrooms(e.target.value)}
                      readOnly
                      className="pl-10 bg-muted/50 cursor-default"
                    />
                  </div>
                </div>
                <div>
                  <Label>Bathrooms</Label>
                  <div className="relative">
                    <Bath className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={valBathrooms}
                      onChange={(e) => setValBathrooms(e.target.value)}
                      readOnly
                      className="pl-10 bg-muted/50 cursor-default"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Floor Area (sqm)</Label>
                  <div className="relative">
                    <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={valFloorArea}
                      onChange={(e) => setValFloorArea(e.target.value)}
                      readOnly
                      className="pl-10 bg-muted/50 cursor-default"
                    />
                  </div>
                </div>
                <div>
                  <Label>Land Area (sqm)</Label>
                  <div className="relative">
                    <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={valLandArea}
                      onChange={(e) => setValLandArea(e.target.value)}
                      readOnly
                      className="pl-10 bg-muted/50 cursor-default"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Garages</Label>
                <Input
                  type="number"
                  value={valGarages}
                  onChange={(e) => setValGarages(e.target.value)}
                  readOnly
                  className="pl-10 bg-muted/50 cursor-default"
                />
              </div>

              {selectedRafid &&
                (feedbackSubmitted ? (
                  <div className="flex items-center justify-center gap-3 py-1">
                    <p className="text-xs text-emerald-600">Thanks for your feedback!</p>
                    <button
                      onClick={() => {
                        setFeedbackSubmitted(false)
                        setFeedbackError('')
                        setFeedbackBedrooms(valBedrooms)
                        setFeedbackBathrooms(valBathrooms)
                        setFeedbackFloorArea(valFloorArea)
                        setFeedbackLandArea(valLandArea)
                        setFeedbackGarages(valGarages)
                        setFeedbackComment('')
                        setShowFeedback(true)
                      }}
                      className="text-xs text-primary underline"
                    >
                      Submit another
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setFeedbackBedrooms(valBedrooms)
                      setFeedbackBathrooms(valBathrooms)
                      setFeedbackFloorArea(valFloorArea)
                      setFeedbackLandArea(valLandArea)
                      setFeedbackGarages(valGarages)
                      setShowFeedback(true)
                    }}
                    className="w-full text-xs text-muted-foreground hover:text-primary transition-colors text-center py-1 flex items-center justify-center gap-1"
                  >
                    <TriangleAlert className="w-3 h-3 text-amber-400" />
                    Something looks wrong? Suggest a correction
                  </button>
                ))}

              {noValuationData && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
                  No estimate available for this property. Try searching for a different address.
                </div>
              )}

              <Button
                onClick={handleValuationSubmit}
                className="w-full"
                disabled={isLoadingEstimate || !selectedRafid}
              >
                {isLoadingEstimate ? 'Getting Estimate...' : 'Get Estimate'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
          {/* ── Results view ── */}
          {(valResult || (propertyDetails && !valResult)) && (
            <div className="space-y-4" ref={scrollToResultsRef}>
              {propertyDetails && !valResult && (
                <Card className="p-4 animate-fade-in">
                  <p className="text-sm text-muted-foreground">
                    No estimate data available for this property.
                  </p>
                </Card>
              )}

              {valResult && (
                <div ref={resultsRef} className="space-y-4 bg-background p-1">
                  {/* ── Property summary (PDF only — hidden on screen) ── */}
                  <div data-pdf-only style={{ display: 'none' }}>
                    <Card className="p-4">
                      <p className="text-sm font-semibold text-foreground mb-3">{valAddress}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                        {valSuburb && (
                          <>
                            <span className="font-medium text-foreground">Suburb</span>
                            <span>{valSuburb}</span>
                          </>
                        )}
                        {valBedrooms && (
                          <>
                            <span className="font-medium text-foreground">Bedrooms</span>
                            <span>{valBedrooms}</span>
                          </>
                        )}
                        {valBathrooms && (
                          <>
                            <span className="font-medium text-foreground">Bathrooms</span>
                            <span>{valBathrooms}</span>
                          </>
                        )}
                        {valFloorArea && (
                          <>
                            <span className="font-medium text-foreground">Floor Area</span>
                            <span>{valFloorArea} sqm</span>
                          </>
                        )}
                        {valLandArea && (
                          <>
                            <span className="font-medium text-foreground">Land Area</span>
                            <span>{valLandArea} sqm</span>
                          </>
                        )}
                        {valGarages && (
                          <>
                            <span className="font-medium text-foreground">Garages</span>
                            <span>{valGarages}</span>
                          </>
                        )}
                      </div>
                    </Card>
                  </div>

                  {/* ── Estimated Value Range ── */}
                  <Card className="p-4 pb-5 bg-gradient-reinz text-primary-foreground animate-fade-in overflow-visible">
                    <h3 className="text-sm font-medium opacity-80 mb-1">Estimated Value Range</h3>
                    <p className="text-2xl font-bold mb-3">
                      {formatPrice(valResult.low)} – {formatPrice(valResult.high)}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <RatingBadge rating={valResult.rating} />
                      <div className="flex items-center gap-3" data-no-capture>
                        <button
                          onClick={handleSave}
                          disabled={isSaved}
                          className="flex items-center gap-1.5 text-xs text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                        >
                          {isSaved ? (
                            <>
                              <BookmarkCheck className="w-4 h-4" /> Saved
                            </>
                          ) : (
                            <>
                              <Bookmark className="w-4 h-4" /> Save Property
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleShare}
                          disabled={isSharing}
                          className="flex items-center gap-1.5 text-xs text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                        >
                          <Share2 className="w-4 h-4" />
                          {isSharing ? 'Preparing...' : 'Share'}
                        </button>
                      </div>
                    </div>
                    {shareError && <p className="text-xs text-red-300 mt-2">{shareError}</p>}
                    <p className="text-3xs opacity-70 pt-4 border-t border-primary-foreground/20">
                      This estimate is computer-generated and should only be used as an indicative
                      value. It is not a registered valuation or a{' '}
                      <a
                        href="https://www.rea.govt.nz/assets/Uploads/Resources/Guides/Code-of-conduct.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Current Market Appraisal (CMA)
                      </a>
                      , which assumes you have physically inspected and takes into account the
                      unique features of the property.
                    </p>
                  </Card>

                  {/* ── Council Valuation ── */}
                  {propertyDetails?.councilValuation && (
                    <Card className="p-4 animate-fade-in">
                      <div className="flex items-center gap-2 mb-3">
                        <Building2 className="w-4 h-4 text-primary" />
                        <h3 className="text-sm font-semibold text-foreground">
                          Council Valuation (RV)
                        </h3>
                      </div>
                      <p className="text-xl font-bold text-primary mb-1">
                        {formatPrice(propertyDetails.councilValuation)}
                      </p>
                      {propertyDetails.revisionDate && (
                        <p className="text-xs text-muted-foreground mb-3">
                          As at {formatDate(propertyDetails.revisionDate)}
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-muted rounded-lg p-2">
                          <p className="text-3xs text-muted-foreground">Land Value</p>
                          <p className="text-sm font-semibold">
                            {propertyDetails.landValue != null
                              ? formatPrice(propertyDetails.landValue)
                              : '—'}
                          </p>
                        </div>
                        <div className="bg-muted rounded-lg p-2">
                          <p className="text-3xs text-muted-foreground">Improvements</p>
                          <p className="text-sm font-semibold">
                            {propertyDetails.improvementValue != null
                              ? formatPrice(propertyDetails.improvementValue)
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* ── Sales History ── */}
                  {propertyDetails?.salesHistory &&
                    propertyDetails.salesHistory.length > 0 &&
                    (() => {
                      const settled = [...propertyDetails.salesHistory]
                        .filter((s) => s.IsSettled && s.SalePrice && s.SaleDate_Date)
                        .sort(
                          (a, b) =>
                            new Date(a.SaleDate_Date!).getTime() -
                            new Date(b.SaleDate_Date!).getTime(),
                        )
                      if (settled.length === 0) return null
                      const lastSale = settled[settled.length - 1]
                      const chartData = settled.map((s) => ({
                        date: new Date(s.SaleDate_Date!).getFullYear().toString(),
                        price: s.SalePrice!,
                        method: s.SaleMethod
                          ? s.SaleMethod.includes(' - ')
                            ? s.SaleMethod.split(' - ')[1]
                            : s.SaleMethod
                          : '',
                      }))
                      return (
                        <Card className="p-4 animate-fade-in">
                          <div className="flex items-center gap-2 mb-1">
                            <TrendingUp className="w-4 h-4 text-primary" />
                            <h3 className="text-sm font-semibold text-foreground">Sales History</h3>
                          </div>
                          <p className="text-xl font-bold text-primary">
                            {formatPrice(lastSale.SalePrice!)}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                            {lastSale.SaleDate_Date && (
                              <span>{formatDate(lastSale.SaleDate_Date)}</span>
                            )}
                            {lastSale.SaleMethod && (
                              <span
                                data-sale-method-badge
                                className="px-2 py-1 bg-muted rounded-md inline-block mt-2"
                              >
                                {lastSale.SaleMethod.includes(' - ')
                                  ? lastSale.SaleMethod.split(' - ')[1]
                                  : lastSale.SaleMethod}
                              </span>
                            )}
                          </div>
                          {settled.length > 1 && (
                            <ResponsiveContainer width="100%" height={120}>
                              <BarChart
                                data={chartData}
                                margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke="#e2e8f0"
                                  vertical={false}
                                />
                                <XAxis
                                  dataKey="date"
                                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis
                                  tickFormatter={(v) =>
                                    v >= 1_000_000
                                      ? `$${(v / 1_000_000).toFixed(1)}M`
                                      : `$${(v / 1_000).toFixed(0)}K`
                                  }
                                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={52}
                                />
                                <Tooltip
                                  formatter={(value: number, _: string, entry: any) => [
                                    formatPrice(value),
                                    entry.payload.method || 'Sale',
                                  ]}
                                  labelStyle={{ fontSize: 11, fontWeight: 600 }}
                                  contentStyle={{
                                    fontSize: 11,
                                    borderRadius: 8,
                                    border: '1px solid #e2e8f0',
                                  }}
                                />
                                <Bar dataKey="price" radius={[4, 4, 0, 0]}>
                                  {chartData.map((_, i) => (
                                    <Cell
                                      key={i}
                                      fill={i === chartData.length - 1 ? '#2563eb' : '#93c5fd'}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          )}
                        </Card>
                      )
                    })()}

                  {/* ── School Zones ── */}
                  {propertyDetails?.schoolZones && propertyDetails.schoolZones.length > 0 && (
                    <Card className="p-4 animate-fade-in">
                      <div className="flex items-center gap-2 mb-3">
                        <GraduationCap className="w-4 h-4 text-primary" />
                        <h3 className="text-sm font-semibold text-foreground">School Zones</h3>
                      </div>
                      <div className="space-y-2">
                        {propertyDetails.schoolZones
                          .filter(
                            (school, i, arr) =>
                              arr.findIndex((s) => s.SchoolName === school.SchoolName) === i,
                          )
                          .map((school, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between py-1.5 border-b border-border last:border-0"
                            >
                              <div>
                                <p className="text-sm font-medium text-foreground">
                                  {school.SchoolName}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {school.Type}
                                  {school.Gender && school.Gender !== 'Co-Ed'
                                    ? ` · ${school.Gender}`
                                    : ''}
                                </p>
                              </div>
                            </div>
                          ))}
                      </div>
                    </Card>
                  )}
                </div>
              )}
            </div>
          )}{' '}
        </TabsContent>

        {/* Saved Properties Tab */}
        <TabsContent value="saved">
          <SavedPropertiesTab
            partyId={partyId}
            onRemove={(rafid) =>
              setSavedRafids((prev) => {
                const next = new Set(prev)
                next.delete(rafid)
                return next
              })
            }
            onOpen={handleLoadSavedProperty}
          />
        </TabsContent>
      </Tabs>

      <PoweredByFooter />

      {/* ── Feedback Bottom Sheet ── */}
      {showFeedback && (
        <div className="fixed inset-0 z-60 flex flex-col justify-center items-center">
          {/* Backdrop — blocks tab bar clicks too */}
          <div className="absolute inset-0 bg-black/50" />

          {/* Sheet — centered, same width as app content */}
          <div className="relative bg-background rounded-2xl shadow-xl p-5 space-y-4 max-h-[80vh] overflow-y-auto w-full max-w-sm mx-4">
            {/* Handle */}
            <div className="w-10 h-1 bg-muted rounded-full mx-auto -mt-1 mb-2" />

            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Suggest a correction</h2>
              <button
                onClick={() => setShowFeedback(false)}
                className="text-muted-foreground hover:text-foreground text-xl leading-none"
              >
                ×
              </button>
            </div>

            <p className="text-xs text-muted-foreground">{valAddress}</p>
            <p className="text-xs text-muted-foreground">
              Edit the fields below to the correct values. Leave unchanged if correct.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Bedrooms</Label>
                <div className="relative">
                  <Bed className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={feedbackBedrooms}
                    onChange={(e) => setFeedbackBedrooms(e.target.value)}
                    className="pl-10 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Bathrooms</Label>
                <div className="relative">
                  <Bath className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={feedbackBathrooms}
                    onChange={(e) => setFeedbackBathrooms(e.target.value)}
                    className="pl-10 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Floor Area (sqm)</Label>
                <div className="relative">
                  <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={feedbackFloorArea}
                    onChange={(e) => setFeedbackFloorArea(e.target.value)}
                    className="pl-10 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Land Area (sqm)</Label>
                <div className="relative">
                  <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={feedbackLandArea}
                    onChange={(e) => setFeedbackLandArea(e.target.value)}
                    className="pl-10 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Garages</Label>
                <Input
                  type="number"
                  value={feedbackGarages}
                  onChange={(e) => setFeedbackGarages(e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Additional comments (optional)</Label>
              <textarea
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value.slice(0, 1000))}
                placeholder="e.g. Land area includes shared driveway, actual usable land is 0..."
                rows={3}
                maxLength={1000}
                className="w-full border rounded px-3 py-2 text-sm bg-background text-foreground resize-none focus:outline-hidden focus:ring-1 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground text-right">
                {feedbackComment.length}/1000
              </p>
            </div>

            {feedbackError && (
              <p className="text-xs text-red-500 text-center -mt-2">{feedbackError}</p>
            )}

            <div className="flex gap-2 pb-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowFeedback(false)
                  setFeedbackError('')
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                disabled={
                  isSubmittingFeedback ||
                  (feedbackBedrooms === valBedrooms &&
                    feedbackBathrooms === valBathrooms &&
                    feedbackFloorArea === valFloorArea &&
                    feedbackLandArea === valLandArea &&
                    feedbackGarages === valGarages &&
                    !feedbackComment)
                }
                onClick={async () => {
                  if (!selectedRafid || isSubmittingFeedback) return
                  setIsSubmittingFeedback(true)
                  setFeedbackError('')
                  try {
                    const res = await fetch('/api/property-feedback', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        rafId: selectedRafid,
                        address: valAddress,
                        userId: partyId,
                        origBedrooms: valBedrooms ? parseInt(valBedrooms) : null,
                        origBathrooms: valBathrooms ? parseInt(valBathrooms) : null,
                        origFloorArea: valFloorArea ? parseFloat(valFloorArea) : null,
                        origLandArea: valLandArea ? parseFloat(valLandArea) : null,
                        origGarages: valGarages ? parseInt(valGarages) : null,
                        corrBedrooms: feedbackBedrooms ? parseInt(feedbackBedrooms) : null,
                        corrBathrooms: feedbackBathrooms ? parseInt(feedbackBathrooms) : null,
                        corrFloorArea: feedbackFloorArea ? parseFloat(feedbackFloorArea) : null,
                        corrLandArea: feedbackLandArea ? parseFloat(feedbackLandArea) : null,
                        corrGarages: feedbackGarages ? parseInt(feedbackGarages) : null,
                        comment: feedbackComment || null,
                      }),
                    })
                    if (!res.ok) {
                      const msg = await res.text()
                      console.error('Feedback submit failed:', res.status, msg)
                      setFeedbackError('Something went wrong. Please try again.')
                      return
                    }
                  } catch (err) {
                    console.error('Feedback submit error:', err)
                    setFeedbackError('Something went wrong. Please try again.')
                    return
                  } finally {
                    setIsSubmittingFeedback(false)
                  }
                  setShowFeedback(false)
                  setFeedbackSubmitted(true)
                  trackEvent('feedback_submitted', { address: valAddress })
                }}
              >
                {isSubmittingFeedback ? 'Submitting…' : 'Submit Feedback'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
